import React, {
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { Tooltip } from '@mui/material';
import {
  gridVisibleSortedRowIdsSelector,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import _ from 'lodash';
import {
  ArrowDownToLine,
  FoldVertical,
  Settings,
  UnfoldVertical,
} from 'lucide-react';

import Button from '../../common/Button';
import { type OutputSChemaDataTable } from '../../common/dataTypes';
import IOSSwitch from '../../common/IOSSwitch';
import Loading from '../../common/Loading';
import { useGetActualDataSchemaFiles } from '../../hooks/tanstack/useActualDataHook';
import { useGetIFSConfig } from '../../hooks/tanstack/useDatasetsHook';
import { useExportModelOutput } from '../../hooks/tanstack/useModelsHook';
import { useGetProjectOutputSchema } from '../../hooks/tanstack/useProjectsHook';
import {
  useGetDistinguishExpenseSets,
  useGetDistinguishModel,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import cn from '../../utils/cn';
import DataGridPremiumTable from '../DataGridPremiumTable';
import { CustomTabPanel, Tab, Tabs } from '../mui-custom';

import ConfigureDriverDialog from './ConfigureDriverDialog';
import CustomMenuDropdown from './CustomMenuDropdown';

export type StyledTabProps = {
  label: string;
  id: string;
};

export const Reports = {
  average_expense: 'Average Expenses',
  average_rate: 'Average Rate',
  stats: 'Stats',
  kpis: 'KPIs',
  financials: 'Financials',
  crew_requirements: 'Crew Requirements',
  movement_matrix: 'Movement Matrix',
  training: 'Training',
  seniority_distribution: 'Seniority Distribution',
};

const ModelTabEnum = {
  average_expense: 0,
  average_rate: 1,
  stats: 2,
  kpis: 3,
  financials: 4,
  crew_requirements: 5,
  movement_matrix: 6,
  training: 7,
  seniority_distribution: 8,
};

export type Report = keyof typeof Reports;

const ModelTable = ({ dataTable }: { dataTable: OutputSChemaDataTable }) => {
  const apiRef = useGridApiRef();

  const [searchParams] = useSearchParams();

  const isActualData = searchParams.get('isActualData') === 'true';

  const [openConfigureDriver, setOpenConfigureDriver] = useState(false);
  const [isExpandAll, setIsExpandAll] = useState(true);

  const { orgId, projectId } = useGetRouteParams();

  const { formState } = useFormContext();

  const { isSubmitting } = formState;

  const report = dataTable.name;

  const { userDefinedExpenseSets } = useGetDistinguishExpenseSets();

  const {
    model,
    modelOutput,
    isLoading: isModelLoading,
    isRunningModelOutputClient,
    isRunningModelOutputServer,
    refetchModelOutput,
  } = useGetDistinguishModel({
    isActual: isActualData,
  });

  const isRevoked = !!model?.revoked_by;

  useEffect(() => {
    if (
      isRunningModelOutputServer === false &&
      isRunningModelOutputClient === true
    ) {
      (async () => {
        refetchModelOutput();
      })();
    }
  }, [isRunningModelOutputServer, isRunningModelOutputClient]);

  let treeData;

  if (report === 'financials') {
    treeData = {
      headerName: 'Expense',
      groupColumns: ['expense_group_name', 'expense_name', 'crew_type'],
    };
  } else if (report === 'kpis') {
    treeData = {
      headerName: 'KPIs',
      groupColumns: ['stats_name', 'crew_type'],
    };
  } else if (report === 'average_expense') {
    treeData = {
      headerName: 'Average Expense',
      groupColumns: ['expense_name', 'crew_type'],
    };
  }

  const modelOutputData = useMemo(
    () => modelOutput?.[report],
    [modelOutput, report]
  );

  const customLabelModelOutputData = useMemo(
    () =>
      Object.entries(modelOutputData ?? {}).reduce(
        (acc, [key, value]) => {
          if (key === 'expense_name') {
            acc[key] = (value as string[]).map((item: string) =>
              userDefinedExpenseSets?.map((u) => u.name).includes(item)
                ? `${item} (custom)`
                : item
            );
          } else {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, number[] | string[]>
      ),
    [modelOutputData, userDefinedExpenseSets]
  );

  const handleToggleGroups = useCallback(() => {
    if (!apiRef.current) {
      return;
    }

    const rowIds = apiRef.current.getAllRowIds();
    const visibleRowsIds = gridVisibleSortedRowIdsSelector(apiRef);

    let expand = true;

    if (rowIds.length === visibleRowsIds.length) {
      setIsExpandAll(false);
      expand = false;
    } else {
      setIsExpandAll(true);
      expand = true;
    }

    rowIds.forEach((rowId) => {
      apiRef.current.setRowChildrenExpansion(rowId, expand);
    });
  }, [apiRef, dataTable]);

  const isLoading = isSubmitting || isModelLoading;

  const actualDataColumns = new Set<string>(
    (modelOutputData?.['__meta__'] as { actual_data_columns?: string[] })
      ?.actual_data_columns ?? []
  );

  const { mutate: exportModelOutput, isPending: isExportingModelOutput } =
    useExportModelOutput();

  return (
    <>
      <div className="flex gap-4 items-center justify-end">
        {modelOutputData && (
          <Button
            variant="outlined_blue"
            className="font-semibold"
            disabled={!exportModelOutput || isExportingModelOutput}
            loading={isExportingModelOutput}
            onClick={() => {
              exportModelOutput({
                orgId,
                projectId,
                modelId: model.id,
                dataName: report,
              });
            }}
          >
            <ArrowDownToLine size={24} />
            <p>Export to Excel</p>
          </Button>
        )}
        {report === 'crew_requirements' && modelOutputData && (
          <>
            <Button
              onClick={() => {
                setOpenConfigureDriver(true);
              }}
              variant="outlined"
            >
              <Settings />
              <p>Configuration</p>
            </Button>
            <ConfigureDriverDialog
              open={openConfigureDriver}
              handleClose={() => {
                setOpenConfigureDriver(false);
              }}
              isLoading={isLoading}
            />
          </>
        )}

        {treeData && modelOutputData && (
          <Button
            onClick={handleToggleGroups}
            variant="outlined"
            className="font-semibold"
          >
            {isExpandAll ? (
              <div className="flex items-center gap-1">
                <FoldVertical strokeWidth={1.5} />
                <p>Collapse All</p>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <UnfoldVertical strokeWidth={1.5} />
                <p>Expand All</p>
              </div>
            )}
          </Button>
        )}
      </div>
      <DataGridPremiumTable
        key={report}
        treeData={treeData}
        apiRef={apiRef}
        data={customLabelModelOutputData}
        actualDataColumns={actualDataColumns}
        columns={dataTable.columns.map((c) => ({
          name: c.name,
          label: c.display_name,
          is_pinned: c.is_pinned,
          styles: c.styles,
          data_type: c.data_type,
        }))}
        placeholder={
          <div className="flex flex-col gap-4 !z-50 items-center justify-center">
            <p className="text-gray-500">Click "Run" to get the output</p>
            <Button disabled={isRevoked} type="submit" variant="contained_red">
              Run
            </Button>
          </div>
        }
        isLoading={isLoading}
      />
    </>
  );
};

export const ModelTablesTab = ({ report }: { report: Report }) => {
  const { orgId, projectId } = useGetRouteParams();

  const { data: outputSchema } = useGetProjectOutputSchema(
    {
      orgId,
      projectId,
    },
    {
      enabled: !!orgId && !!projectId,
    }
  );

  const dataTables = useMemo(
    () => outputSchema?.sections.find((s) => s.name === report)?.data_tables,
    [outputSchema, report]
  );

  return (
    <div className="space-y-10">
      {dataTables?.map((d) => (
        <CustomTabPanel key={d.name} value={report} index={report}>
          <ModelTable dataTable={d} />
        </CustomTabPanel>
      ))}
    </div>
  );
};

const ModelOutputTabs = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const model_tab = searchParams.get('model_tab');
  const isActualData = searchParams.get('isActualData') === 'true';

  const [index, setIndex] = useState(0);

  const [report, setReport] = useState<Report>(
    (model_tab as Report) ?? 'financials'
  );

  const { orgId, projectId } = useGetRouteParams();

  const { data: ifsConfig, isPending: isGettingIfsConfig } = useGetIFSConfig(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!orgId && !!projectId,
    }
  );

  const { model, modelOutput, isRunningModelOutputServer } =
    useGetDistinguishModel();

  const { data: actualDataSchemas } = useGetActualDataSchemaFiles({
    orgId,
    projectId,
  });

  const isActualDataImported = actualDataSchemas?.some((f) => f.is_imported);

  const { data: outputSchema, isLoading: isLoadingProjectOutputSchema } =
    useGetProjectOutputSchema(
      {
        orgId,
        projectId,
      },
      {
        enabled: !!orgId && !!projectId,
      }
    );

  const aircrafts =
    ifsConfig?.aircrafts.map((a) => ({
      label: a.name,
      value: a.name,
    })) ?? [];

  const airports =
    Array.from(new Set(ifsConfig?.aircrafts.map((a) => a.airports).flat())).map(
      (a) => ({
        label: a,
        value: a,
      })
    ) ?? [];

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    const value = event.currentTarget.id as Report;

    setIndex(newValue);
    setReport(value);
  };

  useEffect(() => {
    if (!model_tab) {
      setIndex(0);
      return;
    }

    setIndex((ModelTabEnum as unknown as Record<string, number>)[model_tab]);
  }, [model_tab]);

  const isDisable = model?.is_locked || isRunningModelOutputServer;

  if (isLoadingProjectOutputSchema) {
    return <Loading />;
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex items-center justify-between">
        <Tabs value={index} onChange={handleChange}>
          {outputSchema?.sections?.map((s) => (
            <Tab id={s.name} key={s.name} label={s.display_name} />
          ))}
        </Tabs>
      </div>
      <div className="flex justify-between items-center">
        <div className="flex w-[40%] gap-4">
          {aircrafts && !isGettingIfsConfig && (
            <CustomMenuDropdown
              name={'aircrafts'}
              label={'Aircraft'}
              defaultValue={model?.filters.aircrafts}
              disabled={isDisable}
              data={aircrafts}
            />
          )}
          {airports && !isGettingIfsConfig && (
            <CustomMenuDropdown
              name={'airports'}
              label={'Airport'}
              defaultValue={model?.filters.airports}
              disabled={isDisable}
              data={airports}
            />
          )}
          <Button
            className={cn('cursor-pointer transition h-[36.5px]')}
            disabled={isDisable}
            variant="contained_red"
            type="submit"
          >
            Apply filters
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip
            title={
              !isActualDataImported || _.isEmpty(modelOutput)
                ? 'You need to import actual data and run the model to enable this feature.'
                : ''
            }
            arrow
          >
            <div>
              <IOSSwitch
                disabled={
                  !isActualDataImported || isDisable || _.isEmpty(modelOutput)
                }
                onChange={() => {
                  searchParams.set('isActualData', String(!isActualData));
                  setSearchParams(searchParams);
                }}
                checked={isActualData}
              />
            </div>
          </Tooltip>
          <p className="text-sm text-[#1C252E]">Actual Data</p>
        </div>
      </div>
      {report && (
        <div>
          <ModelTablesTab report={report} />
        </div>
      )}
    </div>
  );
};

export default ModelOutputTabs;
